// /assets/cita_events/js/organizer/payment/organizer_payments.api.js

// -------------------------------------------------------------
// Konstante Namespaces
// -------------------------------------------------------------
import {
  createIdempotencyKey,
  fetchJson,
  buildHeaders,
  normalizeApiResponse,
} from "../../http/utils.js";

const API_PREFIX = '/api/method/';

// Canonical namespace (no payments-wrapper fallback path)
const CONNECT_BASE_PAYMENTS = 'cita_events.api.organizer.stripe_connect';
const IDENTITY_BASE_PAYMENTS = 'cita_events.api.organizer.stripe_identity';

const mapHttpError = (status) => {
  switch (status) {
    case 400: return { code: 'bad_request',       toast: 'Ungültige Anfrage.' };
    case 401: return { code: 'unauthorized',      toast: 'Nicht angemeldet.' };
    case 403: return { code: 'forbidden',         toast: 'Zugriff verweigert.' };
    case 404: return { code: 'not_found',         toast: 'Ressource nicht gefunden.' };
    case 409: return { code: 'conflict',          toast: 'Konflikt bei der Verarbeitung.' };
    case 422: return { code: 'validation_failed', toast: 'Validierungsfehler.' };
    case 429: return { code: 'rate_limited',      toast: 'Zu viele Anfragen.' };
    case 500: return { code: 'internal_error',    toast: 'Interner Serverfehler.' };
    default:  return { code: `http_${status}`,    toast: 'Request fehlgeschlagen.' };
  }
};

const toEnvelope = (payload, { response = null, normalized = null } = {}) => {
  const base = normalized || normalizeApiResponse(payload, { response });
  const data =
    base?.data && typeof base.data === 'object' && !Array.isArray(base.data)
      ? base.data
      : {};

  return {
    success: base.success === true,
    code: base.code || 'request_failed',
    data,
    toast: base.toast || null,
    contractViolation: Boolean(base.contractViolation),
  };
};

const coerceBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (value == null) return fallback;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'false' || normalized === '0' || normalized === '') return false;
    if (normalized === 'true' || normalized === '1') return true;
  }
  return Boolean(value);
};

const coerceCursor = (value) => {
  if (value == null || value === '') return null;
  return value;
};

const normalizePaginated = (payload) => {
  const normalized = toEnvelope(payload);
  const originalData = normalized.data;
  const base = (originalData && typeof originalData === 'object' && !Array.isArray(originalData))
    ? { ...originalData }
    : {};

  const fallbackItems = Array.isArray(originalData)
    ? originalData
    : Array.isArray(base.items)
      ? base.items
      : Array.isArray(base.data)
        ? base.data
        : Array.isArray(base.results)
          ? base.results
          : [];

  if (!Array.isArray(base.items)) {
    base.items = fallbackItems;
  }

  const nextCursor = coerceCursor(base.next_cursor ?? base.nextCursor);
  const prevCursor = coerceCursor(base.prev_cursor ?? base.prevCursor);
  const hasMore = coerceBoolean(base.has_more ?? base.hasMore, false);

  const data = {
    ...base,
    has_more: hasMore,
    next_cursor: nextCursor,
    prev_cursor: prevCursor,
    hasMore,
    nextCursor,
    prevCursor,
  };

  return { ...normalized, data };
};

const shouldSerializeBody = (body) => {
  if (body == null) return false;
  if (typeof body === 'string') return false;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (typeof ArrayBuffer !== 'undefined' &&
      (body instanceof ArrayBuffer || (ArrayBuffer.isView && ArrayBuffer.isView(body)))) return false;
  return typeof body === 'object';
};

export const requestJson = async (url, options = {}) => {
  const controller = new AbortController();
  const timeout = options.timeout ?? 15000;
  const t = setTimeout(() => controller.abort(), timeout);

  try {
    const method = (options.method || 'GET').toUpperCase();
    const init = { ...options };
    delete init.timeout;
    init.method = method;
    init.signal = controller.signal;
    if (method === 'GET' || method === 'HEAD') {
      init.body = undefined;
    }

    const headers = buildHeaders({ json: shouldSerializeBody(init.body), extra: options.headers });
    if (method === 'GET' || method === 'HEAD') {
      delete headers['X-Frappe-CSRF-Token'];
    } else if (!headers['X-Idempotency-Key']) {
      headers['X-Idempotency-Key'] = createIdempotencyKey();
    }

    init.headers = headers;

    const { res: response, payload, normalized: baseNormalized } = await fetchJson(url, init);

    clearTimeout(t);

    const normalized = toEnvelope(payload || {}, {
      response,
      normalized: baseNormalized,
    });

    if (!normalized.success) {
      const mapped = mapHttpError(response.status);
      return {
        success: false,
        code: normalized.code || mapped.code,
        toast: normalized.toast || mapped.toast,
        data: normalized.data || {},
      };
    }
    return normalized;
  } catch (e) {
    clearTimeout(t);
    const aborted = e?.name === 'AbortError';
    return { success: false, code: aborted ? 'timeout' : 'fetch_failed', toast: aborted ? 'Timeout' : 'Netzwerkfehler', data: {} };
  }
};

const postMethod = (method, args = {}, opts = {}) =>
  requestJson(`${API_PREFIX}${method}`, {
    method: 'POST',
    body: args ?? {},
    timeout: opts.timeout ?? 15000,
  });

const getMethod = (method, params = {}, opts = {}) => {
  const q = new URLSearchParams(params ?? {}).toString();
  return requestJson(`${API_PREFIX}${method}${q ? `?${q}` : ''}`, {
    method: 'GET',
    timeout: opts.timeout ?? 15000,
  });
};

// -------------------------------------------------------------
// Helper: zentraler Namespace ohne Legacy-Fallback
// -------------------------------------------------------------
const requestPayments = async (builder) => builder();

// Helper: kompatible Argumente (organizer + organizer_slug)
const orgArgs = (organizer) => ({ organizer, organizer_slug: organizer });

const mapOptionKeys = {
  limit: 'limit',
  startingAfter: 'starting_after',
  endingBefore: 'ending_before',
  status: 'status',
  query: 'query',
  // Backend-Signaturen (stripe_connect.py list_payouts/list_connect_invoices/
  // list_refunds + _prepare_common_list_filters) erwarten created_gte/created_lte.
  // Frappe bindet Query-Params per Funktions-Param-Name → created_from/created_to
  // band nie und der Datumsfilter war wirkungslos.
  createdFrom: 'created_gte',
  createdTo: 'created_lte',
};

const buildListParams = (organizer, options = {}) => {
  const params = { ...orgArgs(organizer) };
  if (!options || typeof options !== 'object') {
    return params;
  }

  Object.entries(mapOptionKeys).forEach(([key, queryKey]) => {
    const value = options[key];
    if (value !== undefined && value !== null) {
      params[queryKey] = value;
    }
  });

  return params;
};

const fetchPaginatedCollection = async (organizer, method, options = {}) => {
  const response = await requestPayments(() =>
    getMethod(`${CONNECT_BASE_PAYMENTS}.${method}`, buildListParams(organizer, options)),
  );

  return normalizePaginated(response);
};

// -------------------------------------------------------------
// CONNECT (Stripe Connect)
// -------------------------------------------------------------
export const getConnectStatus = (organizer) =>
  requestPayments(() => postMethod(`${CONNECT_BASE_PAYMENTS}.get_connect_status`, orgArgs(organizer)));

export const connectStandardStart = (organizer, email = null) =>
  requestPayments(() =>
    postMethod(`${CONNECT_BASE_PAYMENTS}.connect_standard_start`, { ...orgArgs(organizer), email }),
  );

export const createHostedOnboardingLink = (organizer) =>
  requestPayments(() =>
    postMethod(`${CONNECT_BASE_PAYMENTS}.create_hosted_onboarding_link`, orgArgs(organizer), { timeout: 12000 }),
  );

// -------------------------------------------------------------
// IDENTITY (Stripe Identity)
// @deprecated Issue #1054 / FIX-13 (2026-06-02): Diese Identity-Wrapper
// (getIdentityStatus / startIdentityVerification / createHostedIdentityLink /
// getSessionStatus) haben KEINEN UI-Caller — jede Identity-Oberfläche
// (event-wizard, ticket-wizard, subscription dashboard + identity_card) ruft
// die Endpoints direkt via citaFetchJson/frappe.call. Für Backward-Compat
// belassen; in einem Cleanup-Sprint entfernbar.
// -------------------------------------------------------------
export const getIdentityStatus = (organizer) =>
  requestPayments(() =>
    getMethod(`${IDENTITY_BASE_PAYMENTS}.get_identity_status`, orgArgs(organizer)),
  );

export const startIdentityVerification = (organizer) =>
  requestPayments(() =>
    postMethod(`${IDENTITY_BASE_PAYMENTS}.start_identity_verification`, orgArgs(organizer)),
  );

export const createHostedIdentityLink = (organizer) =>
  requestPayments(() =>
    postMethod(`${IDENTITY_BASE_PAYMENTS}.create_hosted_identity_link`, orgArgs(organizer), { timeout: 12000 }),
  );

export const getSessionStatus = (verificationSessionId, sessionToken = null) =>
  requestPayments(() =>
    postMethod(`${IDENTITY_BASE_PAYMENTS}.identity_get_session_status`, {
      verification_session_id: verificationSessionId,
      session_token: sessionToken,
    }),
  );

// -------------------------------------------------------------
// LISTS (Payouts, Invoices, Refunds)
// -------------------------------------------------------------

/**
 * @typedef {Object} StripeListOptions
 * @property {number} [limit] - Maximale Anzahl der Elemente pro Seite.
 * @property {string} [startingAfter] - Cursor für die nächste Seite (`starting_after`).
 * @property {string} [endingBefore] - Cursor für die vorherige Seite (`ending_before`).
 * @property {string} [status] - Filtert nach Statuswerten der Stripe-Objekte.
 * @property {string} [query] - Freitextsuche, falls vom Backend unterstützt.
 * @property {string|number} [createdFrom] - Untere Grenze für `created` (Unix-Timestamp oder ISO-Datum).
 * @property {string|number} [createdTo] - Obere Grenze für `created` (Unix-Timestamp oder ISO-Datum).
 */

/**
 * @template T
 * @typedef {Object} StripePaginatedResponse
 * @property {boolean} success
 * @property {?string} code
 * @property {?string} toast
 * @property {{ items: T[], has_more: boolean, hasMore: boolean, next_cursor: ?string, prev_cursor: ?string, nextCursor: ?string, prevCursor: ?string }} data
 */

/**
 * Lädt Stripe-Auszahlungen für einen Organizer.
 *
 * @param {string} organizer - Slug oder Name des Organizers.
 * @param {StripeListOptions} [options] - Paginierungs- und Filteroptionen.
 * @returns {Promise<StripePaginatedResponse<Object>>} Normalisierte Stripe-Payout-Liste.
 */
export const fetchPayouts = (organizer, options) =>
  fetchPaginatedCollection(organizer, 'list_payouts', options);

/**
 * Lädt Stripe-Rechnungen für einen Organizer.
 *
 * @param {string} organizer - Slug oder Name des Organizers.
 * @param {StripeListOptions} [options] - Paginierungs- und Filteroptionen.
 * @returns {Promise<StripePaginatedResponse<Object>>} Normalisierte Stripe-Invoice-Liste.
 */
export const fetchInvoices = (organizer, options) =>
  fetchPaginatedCollection(organizer, 'list_connect_invoices', options);

/**
 * Lädt Stripe-Erstattungen für einen Organizer.
 *
 * @param {string} organizer - Slug oder Name des Organizers.
 * @param {StripeListOptions} [options] - Paginierungs- und Filteroptionen.
 * @returns {Promise<StripePaginatedResponse<Object>>} Normalisierte Stripe-Refund-Liste.
 */
export const fetchRefunds = (organizer, options) =>
  fetchPaginatedCollection(organizer, 'list_refunds', options);
